import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  START_HOUR as PLAN_START_HOUR,
  END_HOUR as PLAN_END_HOUR,
  SLOT_HEIGHT as PLAN_SLOT_HEIGHT,
  SLOT_MINUTES as PLAN_SLOT_MINUTES,
  PX_PER_HOUR as PLAN_PX_PER_HOUR,
  GRIP_WIDTH as PLAN_GRIP_WIDTH,
  TIME_SLOTS as PLAN_TIME_SLOTS,
  plannerHourColor as plannerHourColorShared,
  durationSlotsFromTask as plannerDurationSlots,
  minutesToEffort as plannerMinutesToEffort,
  dragCreateEndTime as plannerDragCreateEndTime,
} from '../../lib/dashboardPlannerGrid';

/* eslint-disable react/prop-types */

/**
 * DailyPanesShowcase
 * ------------------
 * Pane-system-only preview of the Daily Dashboard three-pane cockpit.
 *
 * IMPORTANT: This showcase intentionally represents task cards as neutral
 * placeholders. Task-card design (open/closed, fields, status controls) is
 * governed elsewhere (OrkaTaskCard + taskFieldGroups). This file is
 * scoped to the PANE system only:
 *
 *   - Day Planner  (timeline rail + slots)
 *   - Today · Unplanned  (committed today, not placed)
 *   - Backlog  (unscheduled waiting lane)
 *
 * Covers: pane identity, counts, effort, headers, loading + empty states,
 * drop zones, desktop 3-pane layout, mobile stacked collapsible panes,
 * and the target implementation API.
 *
 * The CSS is fully scoped under `.orka-daily-panes` so it cannot leak into
 * the rest of the app. The wrapper inherits the current app theme via
 * `document.documentElement` `.dark` class and local density toggles.
 */

const cx = (...parts) => parts.filter(Boolean).join(' ');

function SvgIcon({ children, className = 'dp-icon', ...props }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

const Icons = {
  Clock: (p) => (
    <SvgIcon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l4 2" />
    </SvgIcon>
  ),
  Inbox: (p) => (
    <SvgIcon {...p}>
      <path d="M4 13V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v7" />
      <path d="M4 13l2.2 5h11.6L20 13" />
      <path d="M9 13h6" />
    </SvgIcon>
  ),
  Layers: (p) => (
    <SvgIcon {...p}>
      <path d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path d="m3 12 9 5 9-5" />
      <path d="m3 16 9 5 9-5" />
    </SvgIcon>
  ),
  Plus: (p) => (
    <SvgIcon {...p}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </SvgIcon>
  ),
  Spark: (p) => (
    <SvgIcon {...p}>
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3Z" />
      <path d="M5 17l.8 2.2L8 20l-2.2.8L5 23l-.8-2.2L2 20l2.2-.8L5 17Z" />
    </SvgIcon>
  ),
  Chevron: (p) => (
    <SvgIcon {...p}>
      <path d="m6 9 6 6 6-6" />
    </SvgIcon>
  ),
  Check: (p) => (
    <SvgIcon {...p}>
      <path d="m5 12 4 4L19 6" />
    </SvgIcon>
  ),
};

const paneData = {
  planner: {
    title: 'Day Planner',
    subtitle: 'Scheduled work on a timed rail',
    icon: Icons.Clock,
    tone: 'planner',
    count: 3,
    effort: 8,
    items: [
      { id: 'p1', label: '09:00', size: 'large', muted: false },
      { id: 'p2', label: '11:30', size: 'medium', muted: false },
      { id: 'p3', label: '15:00', size: 'large', muted: true },
    ],
  },
  unplanned: {
    title: 'Today · Unplanned',
    subtitle: 'Committed to today, not placed yet',
    icon: Icons.Inbox,
    tone: 'unplanned',
    count: 3,
    effort: 5,
    items: [
      { id: 'u1', label: '30m', size: 'medium', muted: false },
      { id: 'u2', label: '20m', size: 'small', muted: false },
      { id: 'u3', label: '25m', size: 'small', muted: true },
    ],
  },
  backlog: {
    title: 'Backlog',
    subtitle: 'Unscheduled work waiting to be pulled in',
    icon: Icons.Layers,
    tone: 'backlog',
    count: 4,
    effort: 15,
    items: [
      { id: 'b1', label: '45m', size: 'medium', muted: false },
      { id: 'b2', label: '90m', size: 'large', muted: false },
      { id: 'b3', label: '35m', size: 'medium', muted: true },
      { id: 'b4', label: '75m', size: 'large', muted: false },
    ],
  },
};

const css = `
.orka-daily-panes{font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:var(--dp-text);min-height:0;display:flex;flex-direction:column}
.orka-daily-panes[data-dp-theme="dark"]{--dp-page:#05070c;--dp-surface:#0d1420;--dp-raised:#111a28;--dp-subtle:#172233;--dp-border:rgba(255,255,255,.12);--dp-border-strong:rgba(255,255,255,.22);--dp-text:#f8fafc;--dp-text2:#cbd5e1;--dp-text3:#94a3b8;--dp-shadow:rgba(0,0,0,.42)}
.orka-daily-panes[data-dp-theme="light"]{--dp-page:#e8eef6;--dp-surface:#fff;--dp-raised:#f8fafc;--dp-subtle:#edf2f7;--dp-border:rgba(15,23,42,.18);--dp-border-strong:rgba(15,23,42,.32);--dp-text:#07111f;--dp-text2:#1e293b;--dp-text3:#405169;--dp-shadow:rgba(15,23,42,.13)}
.orka-daily-panes[data-dp-density="compact"]{--dp-gap:8px;--dp-pane-pad:12px;--dp-item-gap:8px;--dp-slot:54px;--dp-body:13px;--dp-meta:11px}
.orka-daily-panes[data-dp-density="standard"]{--dp-gap:14px;--dp-pane-pad:16px;--dp-item-gap:10px;--dp-slot:64px;--dp-body:14px;--dp-meta:12px}
.orka-daily-panes[data-dp-density="calm"]{--dp-gap:20px;--dp-pane-pad:22px;--dp-item-gap:14px;--dp-slot:74px;--dp-body:15px;--dp-meta:13px}
.orka-daily-panes .dp-icon{width:16px;height:16px;flex:none}
.orka-daily-panes .dp-toolbar{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:12px;flex-wrap:wrap}
.orka-daily-panes .dp-seg{display:inline-flex;gap:2px;padding:3px;border:1px solid var(--dp-border);border-radius:999px;background:var(--dp-surface)}
.orka-daily-panes .dp-seg button{height:28px;border:0;border-radius:999px;background:transparent;color:var(--dp-text3);padding:0 10px;font-size:11px;font-weight:800;cursor:pointer;letter-spacing:.03em}
.orka-daily-panes .dp-seg button.active{background:var(--dp-subtle);color:var(--dp-text)}
.orka-daily-panes .dp-meta{color:var(--dp-text3);font-size:var(--dp-meta);line-height:1.45}
.orka-daily-panes .dp-body{color:var(--dp-text2);font-size:var(--dp-body);line-height:1.6}
.orka-daily-panes .dp-h3{margin:0;font-size:15px;color:var(--dp-text);font-weight:700;letter-spacing:-.01em}
.orka-daily-panes .dp-badge{display:inline-flex;align-items:center;gap:6px;min-height:22px;border-radius:999px;border:1px solid var(--dp-border);padding:0 8px;font-size:11px;font-weight:800;white-space:nowrap;background:var(--dp-raised);color:var(--dp-text2)}
.orka-daily-panes .dp-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;height:32px;border:1px solid var(--dp-border);border-radius:10px;background:var(--dp-raised);color:var(--dp-text);font-size:12px;font-weight:800;padding:0 12px;cursor:pointer}
.orka-daily-panes .dp-btn.primary{background:#4f46e5;border-color:#6366f1;color:#fff}
.orka-daily-panes .dp-icon-btn{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border:1px solid var(--dp-border);border-radius:8px;background:var(--dp-raised);color:var(--dp-text);cursor:pointer;padding:0}
.orka-daily-panes .dp-dashboard{border:1px solid var(--dp-border);border-radius:22px;background:linear-gradient(180deg,var(--dp-subtle),var(--dp-surface));padding:var(--dp-pane-pad);box-shadow:0 18px 55px var(--dp-shadow);display:flex;flex-direction:column;min-height:0;flex:1 1 0%}
.orka-daily-panes .dp-dashboard-head{display:flex;justify-content:space-between;gap:10px;align-items:center;margin-bottom:12px;flex-wrap:wrap}
.orka-daily-panes .dp-grid{display:grid;grid-template-columns:1.55fr 1fr;gap:var(--dp-gap);align-items:stretch}
.orka-daily-panes .dp-grid-right{display:grid;grid-template-rows:1fr 1fr;gap:var(--dp-gap);min-width:0}
.orka-daily-panes .dp-grid-3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:var(--dp-gap)}
.orka-daily-panes .dp-grid-2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:var(--dp-gap)}
.orka-daily-panes .dp-pane{border:1px solid var(--dp-border);border-radius:20px;background:var(--dp-surface);min-width:0;min-height:0;height:100%;overflow:hidden;display:flex;flex-direction:column}
.orka-daily-panes .dp-pane[data-tone="planner"]{--tone:#2563eb}
.orka-daily-panes .dp-pane[data-tone="unplanned"]{--tone:#d97706}
.orka-daily-panes .dp-pane[data-tone="backlog"]{--tone:#64748b}
.orka-daily-panes .dp-pane[data-urgency="green"]{border-color:#16a34a;border-width:2px;box-shadow:0 0 0 4px color-mix(in srgb,#16a34a 18%,transparent), 0 8px 22px rgba(22,163,74,.18)}
.orka-daily-panes .dp-pane[data-urgency="blue"]{border-color:#2563eb;border-width:2px;box-shadow:0 0 0 4px color-mix(in srgb,#2563eb 18%,transparent), 0 8px 22px rgba(37,99,235,.18)}
.orka-daily-panes .dp-pane[data-urgency="amber"]{border-color:#f59e0b;border-width:2px;box-shadow:0 0 0 4px color-mix(in srgb,#f59e0b 22%,transparent), 0 8px 22px rgba(245,158,11,.22)}
.orka-daily-panes .dp-pane[data-urgency="red"]{border-color:#dc2626;border-width:2px;box-shadow:0 0 0 4px color-mix(in srgb,#dc2626 22%,transparent), 0 8px 22px rgba(220,38,38,.22)}
.orka-daily-panes .dp-pane-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 14px;border-bottom:1px solid var(--dp-border);background:linear-gradient(180deg,var(--dp-surface),var(--dp-raised));flex:none}
.orka-daily-panes .dp-pane-title-wrap{display:flex;align-items:center;gap:10px;min-width:0;flex:1 1 auto}
.orka-daily-panes .dp-pane-icon{width:32px;height:32px;border-radius:10px;display:grid;place-items:center;background:color-mix(in srgb,var(--tone) 14%,transparent);border:1px solid color-mix(in srgb,var(--tone) 50%,transparent);color:var(--tone)}
.orka-daily-panes .dp-pane-kpis{display:flex;gap:6px;align-items:center;flex-wrap:wrap;justify-content:flex-end;flex:0 0 auto;max-width:60%}
.orka-daily-panes .dp-pane-fs-pill{display:inline-flex;align-items:center;gap:5px;height:22px;border-radius:999px;border:1px solid var(--dp-border);padding:0 8px;font-size:11px;font-weight:800;white-space:nowrap;background:var(--dp-raised);color:var(--dp-text2);cursor:pointer;transition:all .15s ease}
.orka-daily-panes .dp-pane-fs-pill:hover{border-color:var(--dp-text3);color:var(--dp-text)}
.orka-daily-panes .dp-pane-fs-pill[data-active="true"]{background:color-mix(in srgb,var(--dp-accent,#6366f1) 14%,var(--dp-raised));color:var(--dp-text);border-color:color-mix(in srgb,var(--dp-accent,#6366f1) 40%,var(--dp-border))}
.orka-daily-panes .dp-pane-fs-count{display:inline-flex;align-items:center;justify-content:center;min-width:14px;height:14px;padding:0 4px;border-radius:7px;background:var(--dp-text);color:var(--dp-raised);font-size:9px;font-weight:900;line-height:1}
.orka-daily-panes .dp-pane-body{padding:12px;flex:1;min-height:0;overflow:auto}
.orka-daily-panes .dp-pane-body[data-variant="timeline"]{padding:0}
.orka-daily-panes .dp-pane-footer{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 12px;border-top:1px solid var(--dp-border);background:var(--dp-raised);flex:none}
.orka-daily-panes .dp-pane-footer-link{background:transparent;border:0;color:var(--dp-text3);font-size:11px;font-weight:800;cursor:pointer}
.orka-daily-panes .dp-timeline{position:relative;border-radius:16px;background:linear-gradient(180deg,var(--dp-raised),var(--dp-surface));border:1px solid var(--dp-border);padding:8px;min-height:580px}
.orka-daily-panes .dp-slot{display:grid;grid-template-columns:56px 14px minmax(0,1fr);gap:10px;min-height:var(--dp-slot)}
.orka-daily-panes .dp-slot-label{font:11px/1.2 ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--dp-text3);padding-top:8px}
.orka-daily-panes .dp-rail-wrap{position:relative;display:flex;justify-content:center}
.orka-daily-panes .dp-rail{width:4px;border-radius:999px;background:var(--rail-color);opacity:.25;height:100%}
.orka-daily-panes .dp-rail-dot{position:absolute;top:8px;width:8px;height:8px;border-radius:999px;background:var(--rail-color);box-shadow:0 0 0 4px color-mix(in srgb,var(--rail-color) 18%,transparent)}
.orka-daily-panes .dp-slot-line{border-top:1px dashed var(--dp-border)}
.orka-daily-panes .dp-placeholder-layer{position:absolute;top:0;left:82px;right:10px;bottom:0;pointer-events:none}
.orka-daily-panes .dp-task-placeholder{position:absolute;left:0;right:0;min-height:var(--height);border:1px solid color-mix(in srgb,var(--tone) 45%,var(--dp-border));border-radius:14px;background:linear-gradient(180deg,color-mix(in srgb,var(--tone) 16%,var(--dp-surface)),var(--dp-surface));box-shadow:0 10px 24px var(--dp-shadow);padding:10px;display:grid;align-content:start;gap:8px;opacity:var(--opacity)}
.orka-daily-panes .dp-placeholder-title{height:10px;width:72%;border-radius:999px;background:color-mix(in srgb,var(--tone) 45%,var(--dp-border))}
.orka-daily-panes .dp-placeholder-line{height:8px;width:48%;border-radius:999px;background:var(--dp-border-strong)}
.orka-daily-panes .dp-placeholder-meta{display:flex;gap:6px}
.orka-daily-panes .dp-placeholder-chip{width:42px;height:18px;border-radius:999px;background:var(--dp-raised);border:1px solid var(--dp-border)}
.orka-daily-panes .dp-lane{display:grid;gap:var(--dp-item-gap)}
.orka-daily-panes .dp-tl{position:relative;padding:8px}
.orka-daily-panes .dp-tl-slot{display:grid;grid-template-columns:56px 14px minmax(0,1fr);gap:10px;min-height:var(--dp-slot)}
.orka-daily-panes .dp-tl-label{font:11px/1.2 ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--dp-text3);padding-top:8px}
.orka-daily-panes .dp-tl-rail-wrap{position:relative;display:flex;justify-content:center}
.orka-daily-panes .dp-tl-rail{width:4px;border-radius:999px;background:var(--rail-color);opacity:.25;height:100%}
.orka-daily-panes .dp-tl-rail-dot{position:absolute;top:8px;width:8px;height:8px;border-radius:999px;background:var(--rail-color);box-shadow:0 0 0 4px color-mix(in srgb,var(--rail-color) 18%,transparent)}
.orka-daily-panes .dp-tl-line{border-top:1px dashed var(--dp-border)}
.orka-daily-panes .dp-tl-cards{position:absolute;top:8px;left:90px;right:14px;bottom:8px;pointer-events:none}
.orka-daily-panes .dp-tl-card{position:absolute;left:0;right:0;pointer-events:auto;transition:z-index 0s}
/* Clip overflow ONLY on the tall effort-tier cards (180/360/600) where the
   V5 natural body would visually extend past the time slot. The 15/30/60
   cards keep overflow:visible so their content (title, chips, status pill)
   never gets cut off at the bottom edge. */
.orka-daily-panes .dp-tl-card:has(.card.density-180min):not(:has(.card.is-expanded)),
.orka-daily-panes .dp-tl-card:has(.card.density-360min):not(:has(.card.is-expanded)),
.orka-daily-panes .dp-tl-card:has(.card.density-600min):not(:has(.card.is-expanded)){overflow:hidden}
.orka-daily-panes .dp-tl-card:has(.card.is-expanded){z-index:600 !important;overflow:visible}
/* Day-planner timeline cards: closed cards reflect their EXACT slot height
   (E5=360px / E8=720px / E13=1200px). The V3 card body is rendered with
   the canonical fade-peek from the V5 styles — header at the top, natural
   compact body underneath, transparency fades the bottom of the slot.
   Clicking the chevron expands the card via .is-expanded above the slot
   (z-index lifted, .dp-tl-card overflow becomes visible so the expanded
   card breaks out of its hour-slot for editing). */
.orka-daily-panes .dp-tl-card .orka-v3-scope,
.orka-daily-panes .dp-tl-card .orka-v3-scope > .card{height:100% !important}
.orka-daily-panes .dp-tl-card .card.density-180min,
.orka-daily-panes .dp-tl-card .card.density-360min,
.orka-daily-panes .dp-tl-card .card.density-600min{
  height:100% !important;min-height:0 !important;max-height:100% !important;overflow:hidden !important;
}
/* Body in the planner: capped to the remaining slot height so the fade
   ramp ends at the bottom of the 3h/6h/10h block and never spills into
   the hour below. */
.orka-daily-panes .dp-tl-card .card.density-180min:not(.is-expanded) .open-body,
.orka-daily-panes .dp-tl-card .card.density-360min:not(.is-expanded) .open-body,
.orka-daily-panes .dp-tl-card .card.density-600min:not(.is-expanded) .open-body{
  max-height:calc(100% - var(--headerCeiling)) !important;
}
.orka-daily-panes .dp-tl-now{position:absolute;left:0;right:0;height:0;pointer-events:none;z-index:3}
.orka-daily-panes .dp-tl-now-line{position:absolute;left:76px;right:8px;top:-1px;height:2px;background:#ef4444;border-radius:999px;box-shadow:0 0 0 2px rgba(239,68,68,.18)}
.orka-daily-panes .dp-tl-now-dot{position:absolute;left:72px;top:-5px;width:10px;height:10px;border-radius:999px;background:#ef4444;box-shadow:0 0 0 3px rgba(239,68,68,.25)}
.orka-daily-panes .dp-tl-now-label{position:absolute;right:10px;top:-22px;font:10px/1 ui-monospace,SFMono-Regular,Menlo,monospace;color:#fff;background:#ef4444;padding:3px 6px;border-radius:999px;font-weight:800;letter-spacing:.04em}
.orka-daily-panes .dp-tl-tz-cue{position:absolute;left:8px;top:-2px;font:10px/1 ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--dp-text3);background:var(--dp-raised);border:1px solid var(--dp-border);padding:3px 7px;border-radius:999px;font-weight:800;letter-spacing:.04em;z-index:4;pointer-events:none}
/* Dashboard 2 V3 planner interactions (spec: SPEC_DD2_PLANNER_V3.md) */
.orka-daily-panes .dp-tl-interactive{cursor:crosshair;user-select:none}
.orka-daily-panes .dp-tl-interactive.is-dragging{cursor:grabbing}
.orka-daily-panes .dp-tl-hover-line{position:absolute;left:90px;right:14px;height:2px;background:color-mix(in srgb,var(--dp-text-intent,#6366f1) 55%,transparent);border-radius:999px;pointer-events:none;z-index:5;transition:top 60ms linear}
.orka-daily-panes .dp-tl-hover-time{position:absolute;right:-2px;top:-10px;background:color-mix(in srgb,var(--dp-text-intent,#6366f1) 90%,transparent);color:#fff;font:10.5px/1 ui-monospace,SFMono-Regular,Menlo,monospace;padding:3px 7px;border-radius:999px;font-weight:800;letter-spacing:.04em}
.orka-daily-panes .dp-tl-drag-preview{position:absolute;left:90px;right:14px;background:color-mix(in srgb,var(--dp-text-intent,#6366f1) 12%,transparent);border:1px dashed color-mix(in srgb,var(--dp-text-intent,#6366f1) 70%,transparent);border-radius:14px;pointer-events:none;z-index:6}
.orka-daily-panes .dp-tl-drag-preview-label{position:absolute;right:6px;top:6px;font:10.5px/1 ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--dp-text);background:var(--dp-surface);padding:3px 7px;border-radius:999px;border:1px solid var(--dp-border);font-weight:800}
.orka-daily-panes .dp-tl-ghost{position:absolute;left:90px;right:14px;border:2px dashed color-mix(in srgb,var(--dp-text-intent,#6366f1) 75%,transparent);border-radius:14px;background:color-mix(in srgb,var(--dp-text-intent,#6366f1) 8%,transparent);pointer-events:none;z-index:6;display:grid;place-items:center;color:var(--dp-text2);font-weight:800;font-size:12px}
@media (prefers-reduced-motion: reduce){.orka-daily-panes .dp-tl-hover-line{transition:none}}
.orka-daily-panes .dp-lane-placeholder{min-height:var(--height);border:1px solid color-mix(in srgb,var(--tone) 40%,var(--dp-border));border-radius:14px;background:linear-gradient(180deg,color-mix(in srgb,var(--tone) 12%,var(--dp-surface)),var(--dp-surface));padding:10px;display:grid;gap:8px;opacity:var(--opacity)}
.orka-daily-panes .dp-drop-target{border:1px dashed var(--dp-border-strong);border-radius:14px;background:var(--dp-raised);padding:12px;text-align:center;color:var(--dp-text3);font-size:12px;font-weight:800}
.orka-daily-panes .dp-empty{border:1px dashed var(--dp-border-strong);border-radius:16px;background:var(--dp-raised);padding:24px;text-align:center;color:var(--dp-text3)}
.orka-daily-panes .dp-loading-stack{display:grid;gap:10px}
.orka-daily-panes .dp-loading-card{border:1px solid var(--dp-border);border-radius:14px;background:var(--dp-surface);padding:12px;display:grid;gap:8px}
.orka-daily-panes .dp-loading-line{height:9px;border-radius:999px;background:linear-gradient(90deg,var(--dp-subtle),var(--dp-raised),var(--dp-subtle));animation:dp-pulse 1.2s ease-in-out infinite}
@keyframes dp-pulse{0%,100%{opacity:.45}50%{opacity:1}}
.orka-daily-panes .dp-phone{width:min(100%,380px);margin:0 auto;border:1px solid var(--dp-border-strong);border-radius:28px;background:var(--dp-page);padding:12px;box-shadow:0 24px 80px var(--dp-shadow)}
.orka-daily-panes .dp-phone-notch{width:80px;height:4px;border-radius:999px;background:var(--dp-border-strong);margin:2px auto 12px}
.orka-daily-panes .dp-mobile-stack{display:grid;gap:10px}
.orka-daily-panes .dp-mobile-pane{border:1px solid var(--dp-border);border-radius:16px;background:var(--dp-surface);overflow:hidden}
.orka-daily-panes .dp-mobile-pane-head{display:flex;justify-content:space-between;align-items:center;padding:12px;width:100%;background:transparent;border:0;color:inherit;cursor:pointer}
.orka-daily-panes .dp-mobile-pane-body{padding:0 12px 12px}
.orka-daily-panes .dp-code{margin:0;border:1px solid var(--dp-border-strong);border-radius:12px;background:var(--dp-raised);padding:12px;overflow:auto;color:var(--dp-text);font:11.5px/1.55 ui-monospace,SFMono-Regular,Menlo,monospace;white-space:pre-wrap}
.orka-daily-panes .dp-flow{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
@media(max-width:1120px){.orka-daily-panes .dp-grid{grid-template-columns:1fr}.orka-daily-panes .dp-grid-right{grid-template-rows:auto auto}.orka-daily-panes .dp-grid-3,.orka-daily-panes .dp-grid-2{grid-template-columns:1fr}}
@media(max-width:760px){.orka-daily-panes .dp-dashboard-head{align-items:flex-start;flex-direction:column}.orka-daily-panes .dp-timeline{min-height:520px}.orka-daily-panes .dp-slot{grid-template-columns:46px 12px minmax(0,1fr)}.orka-daily-panes .dp-placeholder-layer{left:68px}.orka-daily-panes .dp-pane-head{align-items:flex-start;flex-direction:column}.orka-daily-panes .dp-pane-kpis{justify-content:flex-start}}
`;

function timeColor(hour) {
  if (hour < 13) return '#16a34a';
  if (hour < 16) return '#2563eb';
  if (hour < 19) return '#d97706';
  return '#dc2626';
}

function PlaceholderCard({ item, timeline = false, style = {} }) {
  const heightMap = { small: 62, medium: 86, large: 118 };
  const height = heightMap[item.size] || 86;
  const content = (
    <>
      <div className="dp-placeholder-title" />
      <div className="dp-placeholder-line" />
      <div className="dp-placeholder-meta">
        <div className="dp-placeholder-chip" />
        <div className="dp-placeholder-chip" />
      </div>
    </>
  );
  if (timeline) {
    return (
      <div
        className="dp-task-placeholder"
        style={{ ...style, '--height': `${height}px`, '--opacity': item.muted ? 0.55 : 1 }}
      >
        {content}
      </div>
    );
  }
  return (
    <div
      className="dp-lane-placeholder"
      style={{ '--height': `${height}px`, '--opacity': item.muted ? 0.55 : 1 }}
    >
      {content}
    </div>
  );
}

function Pane({ paneKey, variant = 'normal' }) {
  const pane = paneData[paneKey];
  const Icon = pane.icon;
  const isPlanner = paneKey === 'planner';
  const hours = Array.from({ length: 10 }, (_, i) => i + 8);

  return (
    <div className="dp-pane" data-tone={pane.tone} data-testid={`ds-dp-pane-${paneKey}-${variant}`}>
      <div className="dp-pane-head">
        <div className="dp-pane-title-wrap">
          <div className="dp-pane-icon"><Icon /></div>
          <div>
            <h3 className="dp-h3">{pane.title}</h3>
            <div className="dp-meta">{pane.subtitle}</div>
          </div>
        </div>
        <div className="dp-pane-kpis">
          <span className="dp-badge">{pane.count} items</span>
          <span className="dp-badge">{pane.effort} effort</span>
          <button type="button" className="dp-icon-btn" aria-label="Add">
            <Icons.Plus />
          </button>
        </div>
      </div>
      <div className="dp-pane-body">
        {variant === 'empty' ? (
          <div className="dp-empty">
            <h3 className="dp-h3">Nothing here right now</h3>
            <div className="dp-meta" style={{ marginTop: 4 }}>
              This pane is clear. Keep it calm until work appears.
            </div>
          </div>
        ) : variant === 'loading' ? (
          <div className="dp-loading-stack">
            <div className="dp-loading-card">
              <div className="dp-loading-line" />
              <div className="dp-loading-line" style={{ width: '70%' }} />
            </div>
            <div className="dp-loading-card">
              <div className="dp-loading-line" />
              <div className="dp-loading-line" style={{ width: '45%' }} />
            </div>
          </div>
        ) : isPlanner ? (
          <div className="dp-timeline">
            {hours.map((hour) => (
              <div className="dp-slot" key={hour} style={{ '--rail-color': timeColor(hour) }}>
                <div className="dp-slot-label">{String(hour).padStart(2, '0')}:00</div>
                <div className="dp-rail-wrap">
                  <div className="dp-rail" />
                  <div className="dp-rail-dot" />
                </div>
                <div className="dp-slot-line" />
              </div>
            ))}
            <div className="dp-placeholder-layer">
              {pane.items.map((item, index) => (
                <PlaceholderCard
                  key={item.id}
                  item={item}
                  timeline
                  style={{ top: `${40 + index * 142}px` }}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="dp-lane">
            <div className="dp-drop-target">Drop task here</div>
            {pane.items.map((item) => (
              <PlaceholderCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
      <div className="dp-pane-footer">
        <button type="button" className="dp-pane-footer-link">Pane settings</button>
      </div>
    </div>
  );
}

/* —— Public demo blocks, each rendered by a design-system section —— */

export function DailyPanesToolbar({ theme, density, setTheme, setDensity }) {
  return (
    <div className="dp-toolbar">
      <div className="dp-meta">Pane system uses its own scoped tokens. Theme mirrors the app, density is local.</div>
      <div className="dp-flow">
        <div className="dp-seg" role="tablist" aria-label="Theme">
          <button
            type="button"
            className={theme === 'dark' ? 'active' : ''}
            onClick={() => setTheme('dark')}
            data-testid="ds-dp-theme-dark"
          >
            Dark
          </button>
          <button
            type="button"
            className={theme === 'light' ? 'active' : ''}
            onClick={() => setTheme('light')}
            data-testid="ds-dp-theme-light"
          >
            Light
          </button>
        </div>
        <div className="dp-seg" role="tablist" aria-label="Density">
          {['compact', 'standard', 'calm'].map((d) => (
            <button
              key={d}
              type="button"
              className={density === d ? 'active' : ''}
              onClick={() => setDensity(d)}
              data-testid={`ds-dp-density-${d}`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DailyPanesDesktopLayout() {
  return (
    <div className="dp-dashboard" data-testid="ds-dp-desktop-layout">
      <div className="dp-dashboard-head">
        <div>
          <h3 className="dp-h3">Daily Dashboard</h3>
          <div className="dp-meta">Three-pane cockpit for today&apos;s execution flow</div>
        </div>
        <div className="dp-flow">
          <span className="dp-badge">Focus hours: 09:00–12:00</span>
          <button type="button" className="dp-btn">
            <Icons.Spark /> Let Orka triage
          </button>
        </div>
      </div>
      <div className="dp-grid">
        <Pane paneKey="planner" />
        <div className="dp-grid-right">
          <Pane paneKey="unplanned" />
          <Pane paneKey="backlog" />
        </div>
      </div>
    </div>
  );
}

export function DailyPanesStates() {
  return (
    <div className="dp-grid-3" data-testid="ds-dp-pane-states">
      <Pane paneKey="planner" variant="loading" />
      <Pane paneKey="unplanned" variant="empty" />
      <Pane paneKey="backlog" variant="loading" />
    </div>
  );
}

export function DailyPanesMobileLayout() {
  const panes = useMemo(() => ['planner', 'unplanned', 'backlog'], []);
  const [openPane, setOpenPane] = useState('planner');
  return (
    <div className="dp-phone" data-testid="ds-dp-mobile-layout">
      <div className="dp-phone-notch" />
      <div className="dp-mobile-stack">
        {panes.map((key) => {
          const pane = paneData[key];
          const Icon = pane.icon;
          const isOpen = openPane === key;
          const toneColor = key === 'planner' ? '#2563eb' : key === 'unplanned' ? '#d97706' : '#64748b';
          return (
            <div className="dp-mobile-pane" key={key} data-tone={pane.tone}>
              <button
                type="button"
                className="dp-mobile-pane-head"
                onClick={() => setOpenPane(isOpen ? '' : key)}
                data-testid={`ds-dp-mobile-toggle-${key}`}
              >
                <div className="dp-pane-title-wrap">
                  <div className="dp-pane-icon" style={{ color: toneColor, borderColor: toneColor, background: `color-mix(in srgb, ${toneColor} 14%, transparent)` }}>
                    <Icon />
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <h3 className="dp-h3">{pane.title}</h3>
                    <div className="dp-meta">{pane.count} items · {pane.effort} effort</div>
                  </div>
                </div>
                <Icons.Chevron style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 160ms' }} />
              </button>
              {isOpen && (
                <div className="dp-mobile-pane-body">
                  <Pane paneKey={key} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DailyPanesImplementation() {
  return (
    <div className="dp-grid-2" data-testid="ds-dp-implementation">
      <div>
        <h3 className="dp-h3" style={{ marginBottom: 8 }}>Target API</h3>
        <pre className="dp-code">{`<DailyDashboardPane
  type="planner | unplanned | backlog"
  itemCount={count}
  effortTotal={effort}
  state="loaded | loading | empty | error"
  acceptsDrop={true}
  density="compact | standard | calm"
/>

// Task cards are passed as children/placeholders only.
// Their open/closed logic is owned by OrkaTaskCard elsewhere.`}</pre>
      </div>
      <div>
        <h3 className="dp-h3" style={{ marginBottom: 8 }}>Hard rules</h3>
        <pre className="dp-code">{`// Planner owns timeline rail and time-slot layout.
// Today · Unplanned owns today-but-unscheduled lane behaviour.
// Backlog owns unscheduled backlog lane behaviour.
// Mobile panes collapse / expand independently.`}</pre>
      </div>
    </div>
  );
}

/* Wrapper that scopes CSS, theme and density so any of the above demos
 * can be embedded inside a DSDemoFrame without leaking styles. The theme
 * mirrors the live app theme by watching `<html>.dark` so dark/light never
 * desync from the rest of the page. */
export function DailyPanesScope({ children, defaultTheme = 'dark', defaultDensity = 'standard', withToolbar = false, followAppTheme = true }) {
  const [theme, setTheme] = useState(() => {
    if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) return 'dark';
    return followAppTheme ? 'light' : defaultTheme;
  });
  const [density, setDensity] = useState(defaultDensity);

  useEffect(() => {
    if (!followAppTheme || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const sync = () => setTheme(root.classList.contains('dark') ? 'dark' : 'light');
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, [followAppTheme]);

  return (
    <div className="orka-daily-panes" data-dp-theme={theme} data-dp-density={density}>
      <style>{css}</style>
      {withToolbar && (
        <DailyPanesToolbar theme={theme} density={density} setTheme={setTheme} setDensity={setDensity} />
      )}
      {children}
    </div>
  );
}

/**
 * DailyDashboardPane — DATA-DRIVEN pane primitive exported from the design
 * system. Renders the same pane chrome as the showcase (tone, header, counts,
 * footer) but accepts real children (e.g. <OrkaTaskCard />) instead of
 * placeholders.
 *
 * Props:
 *   type     'planner' | 'unplanned' | 'backlog' — drives tone + default icon
 *   variant  'lane' (default) | 'timeline' — planner cockpit timeline rail
 *   timeline { startHour, endHour, pixelsPerHour } when variant==='timeline'
 *   title    header title (defaults from type)
 *   subtitle optional subtitle line
 *   count    items count badge
 *   effort   effort badge
 *   state    'loaded' | 'loading' | 'empty'
 *   onAdd    optional plus-button handler
 *   footer   optional footer slot (default meta text)
 *   rightSlot optional top-right actions (replaces default plus button)
 *   acceptsDrop  when true, shows a drop target at top of body (lane only)
 *   onDragOver / onDrop pass-through drop handlers
 *
 * Timeline children are wrapped automatically: pass an array of
 *   { key, startHour, durationHours, content } via the `timelineItems` prop
 *   OR pass children with `data-start-hour` / `data-duration-hours` as
 *   numeric attrs and the pane will absolutely position them on the rail.
 */
const PANE_DEFAULTS = {
  planner:   { title: 'Day Planner',        subtitle: 'Scheduled work on a timed rail',    icon: Icons.Clock,  tone: 'planner' },
  unplanned: { title: 'Today · Unplanned',  subtitle: 'Committed to today, not placed yet', icon: Icons.Inbox,  tone: 'unplanned' },
  backlog:   { title: 'Backlog',            subtitle: 'Unscheduled work waiting to be pulled in', icon: Icons.Layers, tone: 'backlog' },
};

function plannerHourColor(hour) {
  if (hour < 12) return '#16a34a';
  if (hour < 15) return '#2563eb';
  if (hour < 18) return '#d97706';
  return '#dc2626';
}

export function DailyDashboardPane({
  type = 'backlog',
  variant = 'lane',
  timeline,
  timelineItems,
  nowHour,
  tzLabel,
  autoScrollToNow = true,
  borderTone,
  title,
  subtitle,
  count,
  effort,
  state = 'loaded',
  onAdd,
  rightSlot,
  acceptsDrop = false,
  onDragOver,
  onDrop,
  footer,
  children,
  testId,
  // V3 planner additions (spec §2):
  onCreateTask,       // (startTime, endTime?, effort?) => void
  onDragDropChange,   // (task, newStart, newEnd) => void
  draggingTask,       // currently-dragged task (from external pane)
  onDragEnd,          // () => void
  activeCardId,       // id of card in inline edit (suppresses hover/click)
}) {
  const def = PANE_DEFAULTS[type] || PANE_DEFAULTS.backlog;
  const Icon = def.icon;
  const resolvedTitle = title ?? def.title;
  const resolvedSubtitle = subtitle ?? def.subtitle;
  const bodyRef = useRef(null);

  // V3 planner: use canonical grid constants when variant==='timeline' & no
  // explicit override is passed. This matches Timeline.jsx (04:00–24:00,
  // 120 px/h). Lane-variant callers fall back to the legacy 7-20 / 64 px.
  const tl = useMemo(() => {
    if (variant === 'timeline' && !timeline) {
      return { startHour: PLAN_START_HOUR, endHour: PLAN_END_HOUR, pxPerHour: PLAN_PX_PER_HOUR };
    }
    return {
      startHour: timeline?.startHour ?? 7,
      endHour: timeline?.endHour ?? 20,
      pxPerHour: timeline?.pixelsPerHour ?? 64,
    };
  }, [timeline, variant]);

  const usesCanonicalGrid = variant === 'timeline'
    && tl.startHour === PLAN_START_HOUR
    && tl.endHour === PLAN_END_HOUR
    && tl.pxPerHour === PLAN_PX_PER_HOUR;

  // Auto-scroll timeline body so `now` (or a sensible default hour) is
  // roughly centered on first render.
  useEffect(() => {
    if (variant !== 'timeline' || !autoScrollToNow || !bodyRef.current) return;
    const hour = (typeof nowHour === 'number' ? nowHour : new Date().getHours()) - tl.startHour;
    if (hour < 0) return;
    const top = Math.max(0, hour * tl.pxPerHour - (bodyRef.current.clientHeight / 2) + tl.pxPerHour / 2);
    bodyRef.current.scrollTop = top;
  }, [variant, autoScrollToNow, nowHour, tl]);

  /* ————————————————— V3 planner interactions ————————————————— */
  const [hoverLineY, setHoverLineY] = useState(null);
  const [hoverTime, setHoverTime] = useState(null);
  const [dragCreate, setDragCreate] = useState(null); // {startSlot,currentSlot}
  const [ghostPosition, setGhostPosition] = useState(null);
  const dragCreateJustEnded = useRef(false);

  // Coarse-pointer / reduced-motion detection (spec §13).
  const coarsePointer = useMemo(() => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches
  ), []);

  const railRelativeY = useCallback((clientY) => {
    if (!bodyRef.current) return null;
    const rect = bodyRef.current.getBoundingClientRect();
    return clientY - rect.top + bodyRef.current.scrollTop;
  }, []);

  const handleTimelineMouseMove = useCallback((e) => {
    if (!usesCanonicalGrid) return;
    if (draggingTask || activeCardId) return;
    if (e.target && e.target.closest && e.target.closest('[data-task-card]')) {
      setHoverLineY(null); setHoverTime(null); return;
    }
    const y = railRelativeY(e.clientY);
    if (y == null) return;
    const slotIndex = Math.floor(y / PLAN_SLOT_HEIGHT);
    if (slotIndex < 0 || slotIndex >= PLAN_TIME_SLOTS.length) {
      setHoverLineY(null); setHoverTime(null); return;
    }
    setHoverLineY(slotIndex * PLAN_SLOT_HEIGHT);
    setHoverTime(PLAN_TIME_SLOTS[slotIndex].time);
    // Extend drag-create range if active
    if (dragCreate) {
      const clamped = Math.max(dragCreate.startSlot, Math.min(slotIndex, PLAN_TIME_SLOTS.length - 1));
      setDragCreate((prev) => prev ? { ...prev, currentSlot: clamped, currentTime: PLAN_TIME_SLOTS[clamped]?.time || prev.currentTime } : prev);
    }
  }, [usesCanonicalGrid, draggingTask, activeCardId, dragCreate, railRelativeY]);

  const handleTimelineMouseLeave = useCallback(() => {
    setHoverLineY(null); setHoverTime(null);
  }, []);

  const handleTimelineClick = useCallback((e) => {
    if (!usesCanonicalGrid) return;
    if (draggingTask || activeCardId) return;
    if (dragCreateJustEnded.current) return;
    if (e.target && e.target.closest && (e.target.closest('[data-task-card]') || e.target.closest('button') || e.target.closest('a'))) return;
    if (!hoverTime || !onCreateTask) return;
    onCreateTask(hoverTime);
  }, [usesCanonicalGrid, draggingTask, activeCardId, hoverTime, onCreateTask]);

  const handleTimelineMouseDown = useCallback((e) => {
    if (!usesCanonicalGrid) return;
    if (coarsePointer) return; // drag-to-create disabled on touch (spec §13)
    if (draggingTask || activeCardId) return;
    if (e.button !== 0) return;
    if (e.target && e.target.closest && (e.target.closest('[data-task-card]') || e.target.closest('button') || e.target.closest('a'))) return;
    const y = railRelativeY(e.clientY);
    if (y == null) return;
    const slotIndex = Math.floor(y / PLAN_SLOT_HEIGHT);
    if (slotIndex < 0 || slotIndex >= PLAN_TIME_SLOTS.length) return;
    setDragCreate({
      startSlot: slotIndex,
      startTime: PLAN_TIME_SLOTS[slotIndex].time,
      currentSlot: slotIndex,
      currentTime: PLAN_TIME_SLOTS[slotIndex].time,
    });
  }, [usesCanonicalGrid, coarsePointer, draggingTask, activeCardId, railRelativeY]);

  const endDragCreate = useCallback(() => {
    if (!dragCreate || !onCreateTask) { setDragCreate(null); return; }
    const { startSlot, currentSlot } = dragCreate;
    const minSlot = Math.min(startSlot, currentSlot);
    const maxSlot = Math.max(startSlot, currentSlot);
    const durationSlots = maxSlot - minSlot + 1;
    const durationMins = durationSlots * PLAN_SLOT_MINUTES;
    const startTime = PLAN_TIME_SLOTS[minSlot].time;
    const endTime = plannerDragCreateEndTime(maxSlot);
    const effortScore = plannerMinutesToEffort(durationMins);
    setDragCreate(null);
    dragCreateJustEnded.current = true;
    setTimeout(() => { dragCreateJustEnded.current = false; }, 100);
    onCreateTask(startTime, endTime, effortScore);
  }, [dragCreate, onCreateTask]);

  useEffect(() => {
    if (!dragCreate) return;
    const up = () => endDragCreate();
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [dragCreate, endDragCreate]);

  // External drag ghost preview (spec §8)
  const handleRailDragOver = useCallback((e) => {
    if (!usesCanonicalGrid || !draggingTask) return;
    e.preventDefault();
    const y = railRelativeY(e.clientY);
    if (y == null) return;
    const rawSlot = Math.floor(y / PLAN_SLOT_HEIGHT);
    const durationSlots = plannerDurationSlots(draggingTask);
    const maxSlot = PLAN_TIME_SLOTS.length - durationSlots;
    const slotIndex = Math.max(0, Math.min(rawSlot, maxSlot));
    setGhostPosition({
      top: slotIndex * PLAN_SLOT_HEIGHT,
      height: durationSlots * PLAN_SLOT_HEIGHT,
      time: PLAN_TIME_SLOTS[slotIndex]?.time,
      slotIndex,
    });
  }, [usesCanonicalGrid, draggingTask, railRelativeY]);

  const handleRailDragLeave = useCallback(() => {
    setGhostPosition(null);
  }, []);

  const handleRailDrop = useCallback((e) => {
    if (!usesCanonicalGrid || !draggingTask || !ghostPosition) {
      setGhostPosition(null); return;
    }
    e.preventDefault();
    const durationSlots = plannerDurationSlots(draggingTask);
    const durationMins = durationSlots * PLAN_SLOT_MINUTES;
    const slot = PLAN_TIME_SLOTS[ghostPosition.slotIndex];
    const startMinutes = slot.hour * 60 + slot.minute;
    const endMinutes = Math.min(startMinutes + durationMins, PLAN_END_HOUR * 60);
    const endHour = Math.floor(endMinutes / 60);
    const endMinute = endMinutes % 60;
    const newStart = slot.time;
    const newEnd = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
    setGhostPosition(null);
    if (onDragEnd) onDragEnd();
    if (onDragDropChange) onDragDropChange(draggingTask, newStart, newEnd);
  }, [usesCanonicalGrid, draggingTask, ghostPosition, onDragEnd, onDragDropChange]);

  const renderTimelineBody = () => {
    const hours = [];
    for (let h = tl.startHour; h <= tl.endHour; h++) hours.push(h);
    const totalPx = (tl.endHour - tl.startHour) * tl.pxPerHour + 24;
    const nowOffset = (typeof nowHour === 'number' && nowHour >= tl.startHour && nowHour <= tl.endHour)
      ? (nowHour - tl.startHour) * tl.pxPerHour + 8
      : null;
    // Format the current time as HH:mm so the now-line label shows the live
    // wall-clock time rather than the timezone (timezone is rendered as a
    // static cue above the start hour — see `dp-tl-tz-cue` below).
    const nowTimeLabel = (typeof nowHour === 'number')
      ? (() => {
          const h = Math.floor(nowHour);
          const m = Math.round((nowHour - h) * 60);
          return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
        })()
      : null;
    const items = timelineItems || [];

    const dragRect = dragCreate ? (() => {
      const a = Math.min(dragCreate.startSlot, dragCreate.currentSlot);
      const b = Math.max(dragCreate.startSlot, dragCreate.currentSlot);
      const durationMins = (b - a + 1) * PLAN_SLOT_MINUTES;
      return {
        top: a * PLAN_SLOT_HEIGHT + 8,
        height: (b - a + 1) * PLAN_SLOT_HEIGHT,
        label: `${PLAN_TIME_SLOTS[a].time} – ${plannerDragCreateEndTime(b)} · E${plannerMinutesToEffort(durationMins)}`,
      };
    })() : null;

    const showOverlays = usesCanonicalGrid;
    const paneTestId = testId || `dp-pane-${type}`;

    return (
      <div
        className={`dp-tl${showOverlays ? ' dp-tl-interactive' : ''}${dragCreate ? ' is-dragging' : ''}`}
        style={{ position: 'relative', minHeight: totalPx }}
        data-testid={`${paneTestId}-body`}
        onMouseMove={showOverlays ? handleTimelineMouseMove : undefined}
        onMouseLeave={showOverlays ? handleTimelineMouseLeave : undefined}
        onMouseDown={showOverlays ? handleTimelineMouseDown : undefined}
        onClick={showOverlays ? handleTimelineClick : undefined}
        onDragOver={showOverlays ? handleRailDragOver : undefined}
        onDragLeave={showOverlays ? handleRailDragLeave : undefined}
        onDrop={showOverlays ? handleRailDrop : undefined}
      >
        {hours.map((hour) => (
          <div
            className="dp-tl-slot"
            key={hour}
            data-testid={`${paneTestId}-slot-${hour}`}
            style={{ '--rail-color': usesCanonicalGrid ? plannerHourColorShared(hour) : plannerHourColor(hour), minHeight: tl.pxPerHour }}
          >
            <div className="dp-tl-label">{String(hour).padStart(2, '0')}:00</div>
            <div className="dp-tl-rail-wrap">
              <div className="dp-tl-rail" />
              <div className="dp-tl-rail-dot" />
            </div>
            <div className="dp-tl-line" />
          </div>
        ))}
        <div className="dp-tl-cards" data-testid={`${paneTestId}-timeline-cards`}>
          {items.map((item) => {
            const top = ((item.startHour - tl.startHour) * tl.pxPerHour) + 8;
            const height = Math.max(item.durationHours * tl.pxPerHour, showOverlays ? PLAN_SLOT_HEIGHT : 60);
            const stackLevel = item.stackLevel || 0;
            const indent = stackLevel * PLAN_GRIP_WIDTH;
            const isActive = activeCardId != null && activeCardId === item.key;
            const style = {
              top,
              height,
              left: indent,
              width: `calc(100% - ${indent}px)`,
              zIndex: isActive ? 500 : (10 + stackLevel),
            };
            return (
              <div
                className="dp-tl-card"
                key={item.key}
                data-task-card="true"
                data-testid={`${paneTestId}-card-${item.key}`}
                data-overnight={item.overnight ? 'true' : undefined}
                data-clipped={item.clipped || undefined}
                style={style}
              >
                {item.content}
              </div>
            );
          })}
        </div>
        {showOverlays && hoverLineY != null && !dragCreate && !ghostPosition && (
          <div
            className="dp-tl-hover-line"
            style={{ top: hoverLineY + 8 }}
            data-testid={`${paneTestId}-hover-line`}
          >
            <span className="dp-tl-hover-time" data-testid={`${paneTestId}-hover-time`}>{hoverTime}</span>
          </div>
        )}
        {showOverlays && dragRect && (
          <div
            className="dp-tl-drag-preview"
            style={{ top: dragRect.top, height: dragRect.height }}
            data-testid={`${paneTestId}-drag-preview`}
          >
            <span className="dp-tl-drag-preview-label">{dragRect.label}</span>
          </div>
        )}
        {showOverlays && ghostPosition && (
          <div
            className="dp-tl-ghost"
            style={{ top: ghostPosition.top + 8, height: ghostPosition.height }}
            data-testid={`${paneTestId}-ghost`}
          >
            {draggingTask?.task_title || draggingTask?.title || 'Drop here'} · {ghostPosition.time}
          </div>
        )}
        {nowOffset != null && (
          <div className="dp-tl-now" style={{ top: nowOffset }} data-testid={`${paneTestId}-now-line`}>
            <div className="dp-tl-now-dot" />
            <div className="dp-tl-now-line" />
            {nowTimeLabel && <div className="dp-tl-now-label">{nowTimeLabel}</div>}
          </div>
        )}
        {/* Timezone cue — anchored just above the first hour row so users see
            the timezone the planner is rendering against without polluting the
            now-line label. */}
        {tzLabel && (
          <div className="dp-tl-tz-cue" data-testid={`${paneTestId}-tz-cue`}>{tzLabel}</div>
        )}
      </div>
    );
  };

  return (
    <div
      className="dp-pane"
      data-tone={def.tone}
      data-urgency={borderTone || undefined}
      data-testid={testId || `dp-pane-${type}`}
      onDragOver={acceptsDrop ? onDragOver : undefined}
      onDrop={acceptsDrop ? onDrop : undefined}
    >
      <div className="dp-pane-head">
        <div className="dp-pane-title-wrap">
          <div className="dp-pane-icon"><Icon /></div>
          <div>
            <h3 className="dp-h3">{resolvedTitle}</h3>
            {resolvedSubtitle && <div className="dp-meta">{resolvedSubtitle}</div>}
          </div>
        </div>
        <div className="dp-pane-kpis">
          {count != null && <span className="dp-badge" data-testid={`dp-pane-${type}-count`}>{count} items</span>}
          {effort != null && <span className="dp-badge" data-testid={`dp-pane-${type}-effort`}>{effort} effort</span>}
          {rightSlot}
          {!rightSlot && onAdd && (
            <button type="button" className="dp-icon-btn" aria-label={`Add to ${resolvedTitle}`} onClick={onAdd} data-testid={`dp-pane-${type}-add`}>
              <Icons.Plus />
            </button>
          )}
        </div>
      </div>
      <div className="dp-pane-body" data-variant={variant} ref={bodyRef}>
        {state === 'loading' ? (
          <div className="dp-loading-stack" aria-busy="true" style={{ padding: 12 }}>
            <div className="dp-loading-card"><div className="dp-loading-line" /><div className="dp-loading-line" style={{ width: '70%' }} /></div>
            <div className="dp-loading-card"><div className="dp-loading-line" /><div className="dp-loading-line" style={{ width: '45%' }} /></div>
          </div>
        ) : state === 'empty' ? (
          <div className="dp-empty" style={{ margin: variant === 'timeline' ? 12 : 0 }}>
            <h3 className="dp-h3">Nothing here right now</h3>
            <div className="dp-meta" style={{ marginTop: 4 }}>This pane is clear. Keep it calm until work appears.</div>
          </div>
        ) : variant === 'timeline' ? (
          renderTimelineBody()
        ) : (
          <div className="dp-lane">
            {acceptsDrop && <div className="dp-drop-target">Drop task here</div>}
            {children}
          </div>
        )}
      </div>
      <div className="dp-pane-footer">
        {footer}
      </div>
    </div>
  );
}

export default function DailyPanesShowcase() {
  return (
    <DailyPanesScope withToolbar>
      <DailyPanesDesktopLayout />
    </DailyPanesScope>
  );
}
